import type { PetUnderwriterType } from "@spotpetinsurance/spot-types/entities/PetQuote";
import type { OutageId } from "@spotpetinsurance/spot-types/events/OutageEvent";

interface TrustpilotConfig {
    [underwriter: string]: {
        BUSINESS_UNIT_ID: string;
        API_KEY: string;
    };
}
export type PTZConfigKeys = "PTZ_US" | "PTZ_CA";

/**
 * Safely parses a numeric environment variable with a fallback value
 */
const parseEnvNumber = (envValue: string | undefined, fallbackValue: number): number => {
    if (envValue === undefined) return fallbackValue;
    const parsed = Number(envValue);
    return isNaN(parsed) ? fallbackValue : parsed;
};

export class PublicConfig {
    // ===============================
    //          GLOBAL VALUES
    // ===============================

    // Spot
    static ENVIRONMENT: string = process.env.NEXT_PUBLIC_ENVIRONMENT ?? "development";
    static IS_LOCAL_ENV: boolean = !!process.env.NEXT_PUBLIC_LOCAL;
    static DEV_ENV = PublicConfig.ENVIRONMENT === "development";
    static CANONICAL_URL_ROOT = process.env.NEXT_PUBLIC_CANONICAL_URL_ROOT || "http://localhost:3000";
    static SPOT_API_ROOT_URL = process.env.NEXT_PUBLIC_SPOT_API_URL ?? "https://dev-api.spotpet.com";
    static UNLIMITED_ANNUAL_LIMIT_VALUE = parseEnvNumber(process.env.NEXT_PUBLIC_UNLIMITED_ANNUAL_LIMIT_VALUE, -1);
    static BASE_PATH = (process.env.NEXT_PUBLIC_BASE_PATH ?? "").trim();
    static MEMBER_LOGIN_URL: string = process.env.NEXT_PUBLIC_MEMBER_LOGIN_URL ?? "https://app.spotpet.com/";
    static DEFAULT_PLAN_MAX_TIMEOUT: number = parseEnvNumber(process.env.NEXT_PUBLIC_DEFAULT_PLAN_MAX_TIMEOUT, 5000);
    static FORCE_FORM_ID_COOKIE_KEY: string = "forceFormId";
    static FORCE_FORM_ID_HEADER_KEY: string = "x-forceFormId";
    static DEV_PARAMS_GLOBAL = {
        EMAIL_REGISTRATION_CHECK: "verifyMC"
    };
    static DEV_ONLY_SPOT_KEY: string | undefined = process.env.NEXT_PUBLIC_DEV_ONLY_SPOT_KEY;

    // Builder.io
    static BUILDER_IO_PUBLIC_API_KEY = process.env.NEXT_PUBLIC_BUILDER_IO_PUBLIC_API_KEY ?? "e660b516b60c4c63a4b427bc0e68aa64";
    static BUILDER_MAX_TIMEOUT = parseEnvNumber(process.env.NEXT_PUBLIC_BUILDER_MAX_TIMEOUT, 5000);
    static IS_BUILDER_ENABLED: boolean = process.env.NEXT_PUBLIC_IS_BUILDER_ENABLED?.toLowerCase() === "true";

    // Castle.io
    static CASTLE_IO_PUBLIC_API_KEY = process.env.NEXT_PUBLIC_CASTLE_API_KEY ?? "pk_Bzq3e34hZibxyGtyqZax7GRVFhN6Essn";

    // DataDog
    static DATADOG_CLIENT_TOKEN = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ?? "pub722e1d5ae91c43c471a840b4c4fe1748";
    static DATADOG_APPLICATION_ID = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID ?? "0772eb70-1279-4f61-95b8-10e164fd0f25";
    static DATADOG_APPLICATION_VERSION = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_VERSION;
    static DATADOG_SESSION_SAMPLE_RATE = parseEnvNumber(process.env.NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE, 100);
    static DATADOG_SESSION_RECORD_RATE = parseEnvNumber(process.env.NEXT_PUBLIC_DATADOG_SESSION_RECORD_RATE, 0);
    static FEATURE_DD_FORCE_RECORDING_ENABLED: boolean = process.env.NEXT_PUBLIC_FEATURE_DD_FORCE_RECORDING_ENABLED === "true";

    // Osano
    static OSANO_CMP_SRC = process.env.NEXT_PUBLIC_OSANO_CMP_SRC ?? "https://cmp.osano.com/AzZnQYUKjmniQ4Zwf/9bed93f2-cdde-4d98-b2ff-48d6f6697c70/osano.js";

    // Intercom
    static INTERCOM_APP_ID = process.env.NEXT_PUBLIC_INTERCOM_APP_ID ?? "y5w7k8ms";

    // Max number of minutes to wait before changing the user's default 'return to quote' step
    static MAX_RETURN_TO_QUOTE_MINUTES = parseEnvNumber(process.env.NEXT_PUBLIC_MAX_RETURN_TO_QUOTE_MINUTES, 60);

    // Refer a friend
    static IS_REFER_A_FRIEND_ENABLED = parseEnvNumber(process.env.NEXT_PUBLIC_IS_REFER_A_FRIEND_ENABLED, 0) === 1;

    // Stripe
    static STRIPE_PUBLISHABLE_KEY =
        process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY ?? "pk_test_51PgEROKCyWsnZgSKM9mevw1JCxqJameQ3aq45qOqQrDHPe3Dw6xeUJ1PeW3uJ21edzDqQ40ktsW6Gbwr5B4TCPjI00Nl9gLt3i";
    // Feature Flags
    static TAKEOVER_PROVISION_PCODES = !!process.env.NEXT_PUBLIC_TAKEOVER_PROVISION_PCODES ? JSON.parse(process.env.NEXT_PUBLIC_TAKEOVER_PROVISION_PCODES) : ["spot_takeovertest"];

    // Statsig
    static STATSIG_API_PROXY_URL = process.env.NEXT_PUBLIC_STATSIG_API_PROXY_URL ?? "https://experiments.spotpet.dev/v1";
    static STATSIG_CLIENT_KEY = process.env.NEXT_PUBLIC_STATSIG_CLIENT_KEY ?? "client-VJfrysoIAnI1eXya73C5ION7PboOVkjGqYjhYneSnkW";
    static STATSIG_MAX_TIMEOUT = parseEnvNumber(process.env.NEXT_PUBLIC_STATSIG_MAX_TIMEOUT, 5000);
    static STATSIG_LAYER_ID_MAIN = process.env.NEXT_PUBLIC_STATSIG_LAYER_ID_MAIN ?? "forms-main";
    static STATSIG_LAYER_DOG_EXPERIMENTS = process.env.NEXT_PUBLIC_STATSIG_LAYER_DOG_EXPERIMENTS ?? "forms-dog-experiments";
    static STATSIG_LAYER_CAT_EXPERIMENTS = process.env.NEXT_PUBLIC_STATSIG_LAYER_CAT_EXPERIMENTS ?? "forms-cat-experiments";
    static STATSIG_LAYER_UPDATE_BILLING = process.env.NEXT_PUBLIC_STATSIG_LAYER_UPDATE_BILLING ?? "forms-update-billing";
    static STATSIG_LAYER_FORBES_EXPERIMENTS = process.env.NEXT_PUBLIC_STATSIG_LAYER_FORBES_EXPERIMENTS ?? "forms-forbes-experiments";
    static STATSIG_LAYER_THANK_YOU_PAGE = process.env.NEXT_PUBLIC_STATSIG_LAYER_THANK_YOU_PAGE ?? "forms-thank-you-page";
    static STATSIG_LAYER_SAMS_LOGIN = process.env.NEXT_PUBLIC_STATSIG_LAYER_SAMS_LOGIN ?? "forms-sams-login";
    static STATSIG_LAYER_WEB_MAIN = process.env.NEXT_PUBLIC_STATSIG_LAYER_WEB_MAIN ?? "web-main";
    static STATSIG_POST_PURCHASE_LAYER = process.env.NEXT_PUBLIC_STATSIG_POST_PURCHASE_LAYER ?? "post-purchase-main";

    // Add any new layers to this array
    static ALL_STATSIG_LAYERS = [
        this.STATSIG_LAYER_CAT_EXPERIMENTS,
        this.STATSIG_LAYER_DOG_EXPERIMENTS,
        this.STATSIG_LAYER_ID_MAIN,
        this.STATSIG_LAYER_UPDATE_BILLING,
        this.STATSIG_LAYER_FORBES_EXPERIMENTS,
        this.STATSIG_LAYER_SAMS_LOGIN,
        this.STATSIG_LAYER_WEB_MAIN,
        this.STATSIG_LAYER_THANK_YOU_PAGE,
        this.STATSIG_POST_PURCHASE_LAYER
    ];
    // Trustpilot
    static TRUSTPILOT_CONFIG: TrustpilotConfig = {
        US: {
            BUSINESS_UNIT_ID: process.env.NEXT_PUBLIC_PTZ_US_TRUSTPILOT_BUSINESS_UNIT_ID ?? "62e828a015be0d5057f853c0",
            API_KEY: process.env.NEXT_PUBLIC_PTZ_US_TRUSTPILOT_API_KEY ?? "KIgtJSyOFkz8hgPWmxLJJsa2vj9A5GmA"
        },
        CA: {
            BUSINESS_UNIT_ID: process.env.NEXT_PUBLIC_PTZ_CA_TRUSTPILOT_BUSINESS_UNIT_ID ?? "66d0bfb3f3f7fe4eab392793",
            API_KEY: process.env.NEXT_PUBLIC_PTZ_CA_TRUSTPILOT_API_KEY ?? "eA7U1uIImvGVOGyAA3qc4NxgR9BnReTr"
        }
    };

    // ===============================
    // FORM-SPECIFIC VALUES BELOW HERE
    // ===============================

    static PTZ_US = {
        // Spot
        FORM_ID: "quote-ptz-us",
        COOKIE_QUOTE_ID_KEY: "quoteIdPtzUs",
        COOKIE_QUOTE_ATTRIBUTES_KEY: "quoteAttributesPtzUs",
        COOKIE_FINALIZE_STARTED_KEY: "spotFinalizeStarted",
        UNDERWRITER: (process.env.NEXT_PUBLIC_PTZ_US_UNDERWRITER as PetUnderwriterType) ?? "ptz-us",
        SPOT_MARKETING_URL: "https://spotpet.com",
        QUOTE_ROOT_URL: process.env.NEXT_PUBLIC_PTZ_US_QUOTE_ROOT ?? (PublicConfig.ENVIRONMENT === "production" ? "https://quote.spotpet.com" : "https://quote.spotpet.dev"),
        SPOT_PERKS_PATH: "/spot-perks-2",
        SAMPLE_POLICY_PATH: "/sample-policy",
        MEMBER_CENTER_PATH: "/member-center",
        MEMBER_LOGIN_URL: process.env.NEXT_PUBLIC_MEMBER_LOGIN_URL ?? "https://app.spotpet.com/login",
        MEMBER_MY_PETS_URL: process.env.NEXT_PUBLIC_MEMBER_MY_PETS_URL ?? "https://app.spotpet.com/my-pets",
        MEMBER_CLAIMS_URL: process.env.NEXT_PUBLIC_MEMBER_CLAIMS_URL ?? "https://app.spotpet.com/claims/start-claim",
        UNDERWRITER_API_URL: process.env.NEXT_PUBLIC_PTZ_US_UNDERWRITER_API_URL ?? "https://api-partner.hartvillegroup.net/quoting",
        UNDERWRITER_API_BASE_URL: process.env.NEXT_PUBLIC_PTZ_US_UNDERWRITER_API_BASE_URL ?? "https://api-partner.hartvillegroup.net",
        DEFAULT_ANNUAL_LIMIT: parseEnvNumber(process.env.NEXT_PUBLIC_PTZ_US_DEFAULT_ANNUAL_LIMIT, 2500),
        DEFAULT_ANNUAL_DEDUCTIBLE: parseEnvNumber(process.env.NEXT_PUBLIC_PTZ_US_DEFAULT_ANNUAL_DEDUCTIBLE, 500),
        DEFAULT_REIMBURSMENT_RATE: parseEnvNumber(process.env.NEXT_PUBLIC_PTZ_US_DEFAULT_REIMBURSMENT_RATE, 70),
        DEFAULT_PLAN_ID: process.env.NEXT_PUBLIC_PTZ_US_DEFAULT_PLAN_ID ?? "Level2~Deductible500~Copay30",
        ACTIVE_OUTAGES: process.env.NEXT_PUBLIC_PTZ_US_ACTIVE_OUTAGES !== undefined ? (JSON.parse(process.env.NEXT_PUBLIC_PTZ_US_ACTIVE_OUTAGES) as OutageId[]) : [],
        FINALIZE_POLLING_INTERVAL: parseEnvNumber(process.env.NEXT_PUBLIC_PTZ_US_FINALIZE_POLLING_INTERVAL, 5000),
        FINALIZE_MAX_TIMEOUT: parseEnvNumber(process.env.NEXT_PUBLIC_PTZ_US_FINALIZE_MAX_TIMEOUT, 32000),

        // DEV ONLY Params for testing
        DEV_PARAMS: {
            FINALIZE_TIMEOUT: "devFinalizeTimeout"
        },

        // Segment
        SEGMENT_WEB_WRITE_KEY: process.env.NEXT_PUBLIC_PTZ_US_SEGMENT_WEB_WRITE_KEY ?? "2hCgEFEOgdrFJGOtwTFSivOjm2jiaUJq",

        // Model Law
        MODEL_LAW_STATES: !!process.env.NEXT_PUBLIC_MODEL_LAW_STATES ? JSON.parse(process.env.NEXT_PUBLIC_MODEL_LAW_STATES) : ["DE", "ME", "MS", "NE", "NH", "WA", "LA"],
        SEGMENT_CDN_URL: process.env.NEXT_PUBLIC_SEGMENT_CDN_URL ?? "https://analytics.spotpet.com",
        SEGMENT_API_URL: process.env.NEXT_PUBLIC_SEGMENT_API_URL ?? "segment.spotpet.com/v1",

        //Forbes quoters
        FORBES_QUOTERS_PCODE: process.env.NEXT_PUBLIC_FORBES_QUOTERS_PCODE?.toLowerCase() ?? "spot_cn1",

        // Stripe, payment processor
        STRIPE_ON_BEHALF_OF_ENABLED: process.env.NEXT_PUBLIC_PTZ_US_STRIPE_ON_BEHALF_OF_ENABLED?.toLowerCase() === "true",
        STRIPE_CONNECTED_ACCOUNT_ID: process.env.NEXT_PUBLIC_PTZ_US_STRIPE_CONNECTED_ACCOUNT_ID ?? "",
        THIRD_PARTY_PAYMENT_PROCESSOR: process.env.NEXT_PUBLIC_PTZ_US_THIRD_PARTY_PAYMENT_PROCESSOR ?? "",

        // US Quote suggestion
        QUOTE_SUGGESTION_ENABLED: process.env.NEXT_PUBLIC_PTZ_US_QUOTE_SUGGESTION_ENABLED?.toLowerCase() === "true",

        // Amazon Toast - auto dismiss timeout
        GIFT_CARD_TOAST_AUTO_DISMISS_TIMEOUT: parseEnvNumber(process.env.NEXT_PUBLIC_PTZ_US_AMAZON_TOAST_AUTO_DISMISS_TIMEOUT, 5000),

        // SAMS Club - Login Form (pre-auth: terms-page CTA defaults to scis.spotpet.com via Contentful customCtaLink)
        SAMS_LOGIN_GIFT_CARD_TERMS_LINK_PATH: "sams-gc-terms",

        // SAMS Club - Quote Form (post-auth: ?returnFormId=quote-ptz-us tells spot-web's CTA helper
        // to point at /quote so middleware preserves quoteId/tracking/Builder cookies). See ENG-16328.
        // Previously pointed at the duplicate "sams-gc-terms-2" Contentful entry; now uses the canonical slug.
        SAMS_QUOTE_GIFT_CARD_TERMS_LINK_PATH: "sams-gc-terms?returnFormId=quote-ptz-us",

        SAMS_RX_TERMS_LINK_PATH: "rx-terms",

        // App store
        APP_STORE_ID: "1480854676",

        APP_STORES: {
            APPLE_URL: process.env.NEXT_PUBLIC_APPLE_URL ?? "https://apps.apple.com/us/app/spot-pet-insurance/id1480854676",
            GOOGLE_URL: process.env.NEXT_PUBLIC_GOOGLE_URL ?? "https://play.google.com/store/apps/details?id=com.cfpetinsurance.spot.sandpiper&hl=en_US"
        }
    };

    static PTZ_CA = {
        // Spot
        FORM_ID: "quote-ptz-ca",
        UNDERWRITER: (process.env.NEXT_PUBLIC_PTZ_CA_UNDERWRITER as PetUnderwriterType) ?? "ptz-ca",
        SPOT_MARKETING_URL: "https://spotpetinsurance.ca",
        QUOTE_ROOT_URL: PublicConfig.ENVIRONMENT === "production" ? "https://quote.spotpetinsurance.ca" : "https://dev-quote.spotpetinsurance.ca",
        SAMPLE_POLICY_PATH: "/spot-sample-policy-ca.pdf",
        DEFAULT_ANNUAL_LIMIT: parseEnvNumber(process.env.NEXT_PUBLIC_PTZ_CA_DEFAULT_ANNUAL_LIMIT, 5000),
        DEFAULT_PLAN_ID: process.env.NEXT_PUBLIC_PTZ_CA_DEFAULT_PLAN_ID ?? "C_SPOT_L2_DOG",
        COOKIE_QUOTE_ID_KEY: "quoteIdPtzCa",
        COOKIE_QUOTE_ATTRIBUTES_KEY: "quoteAttributesPtzCa",
        // Session cookie set when a finalize begins so a page reload mid/after finalize resumes a status
        // re-check instead of handing back a fresh payment form (double-charge guard). Mirrors PTZ_US.
        COOKIE_FINALIZE_STARTED_KEY: "spotCaFinalizeStarted",
        // Segment
        SEGMENT_WEB_WRITE_KEY: process.env.NEXT_PUBLIC_PTZ_CA_SEGMENT_WEB_WRITE_KEY ?? "FoXOH1vYoL92VYtcjCoU0Fy58HxAtE2z",
        // TODO: we need to move this to a CA URL
        SEGMENT_CDN_URL: process.env.NEXT_PUBLIC_SEGMENT_CDN_URL ?? "https://analytics.spotpet.com",
        SEGMENT_API_URL: process.env.NEXT_PUBLIC_SEGMENT_API_URL ?? "segment.spotpet.com/v1",

        // CA Quote suggestion
        QUOTE_SUGGESTION_ENABLED: process.env.NEXT_PUBLIC_PTZ_CA_QUOTE_SUGGESTION_ENABLED?.toLowerCase() === "true",

        // Skip optimistic pricing on coverage changes when the quote has discounts — Orca's discount
        // amounts don't reconcile with charged prices, so local math lands off. Un-discounted quotes stay optimistic.
        DISABLE_OPTIMISTIC_DISCOUNT_PRICING: (process.env.NEXT_PUBLIC_PTZ_CA_DISABLE_OPTIMISTIC_DISCOUNT_PRICING ?? "true").toLowerCase() === "true",

        // Use the API's `netPrice` as the settled per-pet price instead of deriving base − discountAmount.
        // Passed to petQuoteToQuote by the CA form's conversion call sites; independent of the flag above.
        SHOW_NET_PRICE: (process.env.NEXT_PUBLIC_PTZ_CA_SHOW_NET_PRICE ?? "true").toLowerCase() === "true",

        PAYMENT_API_BASE_URL: process.env.NEXT_PUBLIC_PTZ_CA_PAYMENT_API_BASE_URL ?? "https://api.ca.petpartners.com",
        PAYMENT_IFRAME_URL: process.env.NEXT_PUBLIC_PTZ_CA_PAYMENT_IFRAME_URL ?? "https://chase.hostedpaymentservice.net/hpf/",
        PAYMENT_LOAD_SCRIPT_URL: process.env.NEXT_PUBLIC_PTZ_CA_PAYMENT_LOAD_SCRIPT_URL ?? "https://chase.hostedpaymentservice.net/includes/hpfParent.min.js",

        // Finalize status-poll window (useChasePayment): attempts × delay bounds how long a lost/failed
        // finalize response is reconciled against the quote status before the call-center state renders.
        // Wider than the original 3×2s because slow policy binds can outlive the 29s API Gateway
        // integration timeout (the dominant finalize 5xx) and land shortly after. Env-tunable, mirroring
        // the US FINALIZE_POLLING_INTERVAL / FINALIZE_MAX_TIMEOUT pair.
        FINALIZE_STATUS_POLL_ATTEMPTS: parseEnvNumber(process.env.NEXT_PUBLIC_PTZ_CA_FINALIZE_STATUS_POLL_ATTEMPTS, 5),
        FINALIZE_STATUS_POLL_DELAY_MS: parseEnvNumber(process.env.NEXT_PUBLIC_PTZ_CA_FINALIZE_STATUS_POLL_DELAY_MS, 3000),

        // DEV ONLY (non-production): window-global key Playwright sets to shrink the finalize status-poll
        // delay so the poll-driven checkout tests don't wait the real interval. Read by useChasePayment.
        DEV_PARAMS: {
            FINALIZE_POLL_DELAY_GLOBAL: "__spotDevFinalizePollDelayMs"
        }
    };

    static PTZ_US_FALLBACK = {
        FORM_ID: "quote-fallback-us"
    };

    static PTZ_US_RETARGET = {
        FORM_ID: "quote-retarget-us"
    };

    static UPDATE_BILLING = {
        FORM_ID: "update-billing",
        FORCE_DD_RECORDING: process.env.NEXT_PUBLIC_UPDATE_BILLING_FORCE_DD_RECORDING?.toLowerCase() === "true",
        SEGMENT_WEB_WRITE_KEY: process.env.NEXT_PUBLIC_UPDATE_BILLING_SEGMENT_WEB_WRITE_KEY ?? "D0hHWRvdzBhznYT0d7uQvwpTcxIjGh5a",
        MPAN_ENABLED: process.env.NEXT_PUBLIC_UPDATE_BILLING_MPAN_ENABLED?.toLowerCase() === "true",
        STRIPE_APPLE_PAY_ENABLED: process.env.NEXT_PUBLIC_UPDATE_BILLING_STRIPE_APPLE_PAY_ENABLED?.toLowerCase() === "true",
        STRIPE_GOOGLE_PAY_ENABLED: process.env.NEXT_PUBLIC_UPDATE_BILLING_STRIPE_GOOGLE_PAY_ENABLED?.toLowerCase() === "true"
    };

    static SAMS_LOGIN = {
        FORM_ID: "sams",
        URL: process.env.NEXT_PUBLIC_SAMS_LOGIN_URL ?? "https://quote.spotpet.dev/forms/sams"
    };

    static QUOTE_WIDGET_US = {
        FORM_ID: "quote-widget-us",
        PHONE_ENABLED: process.env.NEXT_PUBLIC_QUOTE_WIDGET_US_PHONE_ENABLED?.toLowerCase() === "true"
    };

    static ADD_A_PET = {
        FORM_ID: "add-a-pet",
        /**
         * Comma-separated allowlist of permitted parent origins for postMessage.
         * Each entry may use a `*` glob in the host portion (single subdomain
         * segment, e.g. `https://*.eas-deploy-preview.spotpet.dev`). The first
         * exact (non-glob) entry is used as the canonical parent origin when
         * the form has not yet resolved one via referrer/inbound message.
         * Defaults to the prod parent origin when unset.
         */
        PARENT_ORIGINS: process.env.NEXT_PUBLIC_ADD_A_PET_PARENT_ORIGINS ?? "https://app.spotpet.com",
        ALLOW_MULTIPLE_PETS: process.env.NEXT_PUBLIC_ADD_A_PET_ALLOW_MULTIPLE_PETS?.toLowerCase() === "true"
    };
}
